import { Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppGtmService } from '@app/shared/services/gtm.service';

const DEFAULT_META_IMAGE = 'coinmate.jpg';
const META_IMAGE_WIDTH = '1200';
const META_IMAGE_HEIGHT = '630';
const IMAGES_PATH = 'https://coinmate.io/assets/img/';

@Injectable({
  providedIn: 'root',
})
export class MetaTagsService implements OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private renderer: Renderer2;
  private defaultTitleKey = 'meta-tags.homepage.meta-title';
  private titleSuffixKey = 'meta-tags.title-suffix';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private gtmService: AppGtmService,
    private translate: TranslateService,
    @Inject(RendererFactory2) private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private updateGtmTag(title: string): void {
    const gtmTag = {
      event: 'ga_pageview',
      path: this.document.location.pathname,
      title,
    };
    this.gtmService.pushTag(gtmTag).then();
  }

  /**
   * Sets title and meta tags for the current page based on translation keys.
   *
   * @param titleKey - Translation key for the page title. If null, sets default title.
   * @param interpolateParams - Optional parameters to interpolate into translated strings.
   * @param metaImage - Optional URL for social media preview image.
   *
   * The method handles:
   * - Setting document title with suffix
   * - Setting meta title and description if translations exist
   * - Adding OpenGraph and Twitter card meta tags
   * - Adding hreflang tags for localization
   * - Adding canonical URL
   * - Preventing indexing if meta translations missing
   * - Updating GTM tracking
   */
  setTitleAndMetaTags(titleKey: string | null, interpolateParams = {}, metaImage?: string) {
    if (!titleKey) {
      this.setDefaultTitle();
      return;
    }

    const metaTagsPrefix = titleKey.endsWith('.title') ? titleKey.slice(0, -'title'.length) : titleKey + '.';
    const metaTitleKey = metaTagsPrefix + 'meta-title';
    const metaDescriptionKey = metaTagsPrefix + 'meta-description';

    this.translate
      .get([titleKey, this.titleSuffixKey, metaTitleKey, metaDescriptionKey], interpolateParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((translations) => {
        this.renderer.setAttribute(document.documentElement, 'lang', this.translate.currentLang);

        const title = `${translations[titleKey]} ${translations[this.titleSuffixKey]}`;
        this.titleService.setTitle(title);

        // If meta title and description is not present, treat the page as not indexed.
        const isIndexed =
          translations[metaTitleKey] !== metaTitleKey && translations[metaDescriptionKey] !== metaDescriptionKey;

        this.removeMetaTags();

        if (isIndexed) {
          const metaTitle = `${translations[metaTitleKey]} ${translations[this.titleSuffixKey]}`;
          const metaDescription = translations[metaDescriptionKey];
          const fullUrl = this.document.location.href;
          this.addMetaTitleAndDescription(metaTitle, metaDescription);
          this.addOpenGraphAndTwitterCards(metaTitle, metaDescription, fullUrl, metaImage);
          this.addHrefLangTags(fullUrl);
          this.setCanonicalTag();
        } else {
          this.addRobotPreventionTags();
          this.removeCanonicalTag();
        }

        // Update GTM
        this.updateGtmTag(title);
      });
  }

  private setDefaultTitle(): void {
    this.translate
      .get([this.defaultTitleKey, this.titleSuffixKey])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((translations) => {
        const title = `${translations[this.defaultTitleKey]} ${translations[this.titleSuffixKey]}`;
        this.titleService.setTitle(title);
      });
  }

  private removeMetaTags(): void {
    this.metaService.removeTag('name="description"');
    this.metaService.removeTag('name="title"');
    this.metaService.removeTag('name="robots"');
    this.removeOpenGraphAndTwitterCards();
    this.removeHrefLangTags();
  }

  private addMetaTitleAndDescription(title: string, description: string): void {
    this.metaService.updateTag(
      {
        name: 'description',
        content: description,
      },
      "name='description'",
    );
    this.metaService.updateTag(
      {
        name: 'title',
        content: title,
      },
      "name='title'",
    );
  }

  private addOpenGraphAndTwitterCards(
    metaTitle: string,
    metaDescription: string,
    metaUrl: string,
    metaImage = DEFAULT_META_IMAGE,
  ): void {
    this.metaService.updateTag({ property: 'og:title', content: metaTitle });
    this.metaService.updateTag({ property: 'og:description', content: metaDescription });
    this.metaService.updateTag({
      property: 'og:image',
      content: `${IMAGES_PATH}${metaImage}`,
    });
    this.metaService.updateTag({ property: 'og:image:width', content: META_IMAGE_WIDTH });
    this.metaService.updateTag({ property: 'og:image:height', content: META_IMAGE_HEIGHT });
    this.metaService.updateTag({ property: 'og:url', content: metaUrl });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });

    this.metaService.updateTag({ name: 'twitter:title', content: metaTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: metaDescription });
    this.metaService.updateTag({
      name: 'twitter:image',
      content: `${IMAGES_PATH}${metaImage}`,
    });
    this.metaService.updateTag({ name: 'twitter:image:width', content: META_IMAGE_WIDTH });
    this.metaService.updateTag({ name: 'twitter:image:height', content: META_IMAGE_HEIGHT });
    this.metaService.updateTag({ name: 'twitter:creator', content: '@CoinmateBT' });
    this.metaService.updateTag({ name: 'twitter:site', content: '@CoinmateBT' });
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
  }

  private addHrefLangTag(lang: string, url: string): void {
    const head = this.document.head;
    const linkElement = this.renderer.createElement('link');

    this.renderer.setAttribute(linkElement, 'rel', 'alternate');
    this.renderer.setAttribute(linkElement, 'hreflang', lang);
    this.renderer.setAttribute(linkElement, 'href', url);

    this.renderer.appendChild(head, linkElement);
  }

  private addHrefLangTags(fullUrl: string): void {
    const langRegex = /\/(?<lang>cs|en)(\/|$)/;
    const match = fullUrl.match(langRegex);

    if (match && match.groups) {
      const { lang } = match.groups;
      const csUrl = fullUrl.replace(lang, 'cs');
      const enUrl = fullUrl.replace(lang, 'en');
      const defaultUrl = enUrl;

      this.addHrefLangTag('cs', csUrl);
      this.addHrefLangTag('en', enUrl);
      this.addHrefLangTag('x-default', defaultUrl);
    }
  }

  private setCanonicalTag(): void {
    const location = this.document.location;
    let canonicalLink = this.document.querySelector('link[rel="canonical"]');

    const url = `${location.origin}${location.pathname}`;
    const head = this.document.head;

    if (!canonicalLink) {
      canonicalLink = this.renderer.createElement('link');
      this.renderer.setAttribute(canonicalLink, 'rel', 'canonical');
      this.renderer.appendChild(head, canonicalLink);
    }

    this.renderer.setAttribute(canonicalLink, 'href', url);
  }

  private removeCanonicalTag() {
    const canonicalLink = this.document.querySelector('link[rel="canonical"]');
    canonicalLink?.remove();
  }

  private removeOpenGraphAndTwitterCards(): void {
    const ogTags = ['og:title', 'og:description', 'og:image', 'og:image:width', 'og:image:height', 'og:url', 'og:type'];
    ogTags.forEach((tag) => {
      this.metaService.removeTag(`property='${tag}'`);
    });

    const twitterTags = [
      'twitter:title',
      'twitter:description',
      'twitter:image',
      'twitter:image:width',
      'twitter:image:height',
      'twitter:creator',
      'twitter:site',
      'twitter:card',
    ];
    twitterTags.forEach((tag) => {
      this.metaService.removeTag(`name='${tag}'`);
    });
  }

  private removeHrefLangTags(): void {
    const head = this.document.head;
    const linkElements = head.querySelectorAll('link[rel="alternate"][hreflang]');

    linkElements.forEach((linkElement) => {
      this.renderer.removeChild(head, linkElement);
    });
  }

  private addRobotPreventionTags(): void {
    this.metaService.updateTag(
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
      "name='robots",
    );
  }
}
